import { useNavigate, useParams } from "react-router-dom";
import { useAppContext } from "../../../../../AppContext";
import {
  isStoreOnlyStudio,
  studioHidesPlans,
  studioHidesBodyMetrics,
  studioHidesWorkouts,
  studioHidesGoals,
} from "../../../../../common/infrastructure/helpers/studioHelper";
import { IconButton } from "../../../../../common/components/button/iconButton/IconButton";
import { NotesButton } from "../../../../../common/components/common/NotesDialog/NotesButton";
import {
  AppDropdownMenu,
  IDropdownMenuItem,
} from "../../../../../common/components/AppDropdownMenu/AppDropdownMenu";
import { showCustomConfirmDialog } from "../../../../../common/components/dialogs/AppConfirmDialog";
import { requiresStudioSwitchConfirmation } from "../../../../../common/infrastructure/utilities/studioAccessUtility";
import { getCurrentUserRole } from "../../../../../common/infrastructure/helpers/userHelper";
import { Icon, IconType } from "../../../../../common/icons/Icon";
import {
  IfFalse,
  IfTrue,
  RenderWhen,
} from "../../../../../common/infrastructure/components/RenderWhen";
import { useAddAdditionalLayoutButton } from "../../../../../common/infrastructure/hooks/useAddAdditionalLayoutButton";
import { useApiMutation } from "../../../../../common/infrastructure/hooks/useApiMutation";
import { useApiQuery } from "../../../../../common/infrastructure/hooks/useApiQuery";
import { usePermissions } from "../../../../../common/infrastructure/hooks/usePermissions";
import { endpointsMetadata } from "../../../../../common/infrastructure/routes/endpointsMetadata";
import {
  constructEditUserHref,
  getDefaultRoutePath,
  routePaths,
} from "../../../../../common/infrastructure/routes/routePaths";
import { useMemo, useState } from "react";
import { userAdministrationUserConfirmMessages } from "../helpers/userAdministrationUserConfirmMessages";
import { IUserModel } from "../models/userModel";
import { IEventHistoryItemViewModel } from "../../../../../common/models/eventHistoryItemViewModel";
import {
  constructCashdeskHref,
  constructPlannerEventHref,
} from "../../../../../common/infrastructure/routes/routePaths";
import { HistoryItemKind } from "../../../../../common/enums";
import styles from "../UserAdministrationUser.module.scss";
import { useAuthenticationFunctions } from "../../../../authentication/utils/AuthenticationUtility";
import { IStudioSettingsModel } from "../../../../../common/models/IStudioSettingsModel";
import {
  formatSignedEurValue,
  getAmountTone,
} from "../../../../../common/utilities/currencyUtility";
import { useCardRenewalOptions } from "../components/RenewCardDialog/hooks/useCardRenewalOptions";

export const useUserAdministrationUser = () => {
  const [showPlansPopup, setShowPlansPopup] = useState(false);
  const [showAdditionalDetailsPopup, setShowAdditionalDetailsPopup] =
    useState(false);
  const [showBulkDeletePopup, setShowBulkDeletePopup] = useState(false);
  const [showDepositPopup, setShowDepositPopup] = useState(false);
  const [showFormsPopup, setShowFormsPopup] = useState(false);
  const [showHistoryPopup, setShowHistoryPopup] = useState(false);
  const [showMergePopup, setShowMergePopup] = useState(false);
  const [showRenewCardPopup, setShowRenewCardPopup] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    selectedStudioId,
    selectedStudio,
    currentUser,
    userStudios,
    requestStudioSwitch,
  } = useAppContext();
  const isStoreOnly = isStoreOnlyStudio(selectedStudio);
  const showPlans = !isStoreOnly && !studioHidesPlans(selectedStudio);
  const showBodyMetrics = !isStoreOnly && !studioHidesBodyMetrics(selectedStudio);
  // Follows the studio's "Обектът не използва тренировки" setting only: shown for every
  // client, with or without workouts. Unlike plans and metrics it is not dropped for a
  // shop-only studio, whose clients still see "Моите тренировки" and can log workouts.
  const showWorkouts = !studioHidesWorkouts(selectedStudio);
  const showGoals = !isStoreOnly && !studioHidesGoals(selectedStudio);
  // The studio's "Обектът използва формуляри": forms filled before a reservation or sent
  // from here. The setting is not offered to a shop-only studio.
  const showForms =
    !isStoreOnly && selectedStudio?.usesPreReservationForms === true;
  const { markAsUnreliableConfirmMessage } =
    userAdministrationUserConfirmMessages();

  const {
    userHasPermissionsToGeneratePasswordResetLink,
    onTryGenerateAndCopyPasswordResetLink,
  } = useAuthenticationFunctions();

  const { userHavePermissions } = usePermissions();

  const userHasPermissionsToBulkDeleteUserEvents = useMemo(
    () =>
      userHavePermissions(
        endpointsMetadata.planner.bulkPermanentlyDeleteUserEvents,
      ),
    [currentUser],
  );

  const userHasPermissionsToMergeUsers = useMemo(
    () => userHavePermissions(endpointsMetadata.user.mergeUsers),
    [currentUser],
  );

  const userHasPermissionsToRenewCard = useMemo(
    () => userHavePermissions(endpointsMetadata.plan.renewCard),
    [currentUser],
  );

  const { data: user, isLoading } = useApiQuery<IUserModel>({
    queryKey: [endpointsMetadata.user.getUser, id],
    endpoint: `${endpointsMetadata.user.getUser}?userId=${id}`,
    enabled: id !== undefined,
    globalIsLoading: false,
  });

  // "Поднови карта" is asked about only for a live client who has a card at all; the item
  // then shows only when one of the cards can actually be renewed.
  const mayRenewCard =
    !!user &&
    userHasPermissionsToRenewCard &&
    showPlans &&
    !user.isCancelled &&
    (user.plans ?? []).some((plan) => !plan.isDeleted);

  const { data: cardRenewalOptions } = useCardRenewalOptions(
    user?.id,
    mayRenewCard,
  );

  const canRenewCard = mayRenewCard && (cardRenewalOptions?.length ?? 0) > 0;

  const depositBalance = user?.depositBalance ?? 0;
  const depositBalanceText = formatSignedEurValue(depositBalance);
  const depositBalanceTone = getAmountTone(depositBalance);

  const { data: tenantSettings } = useApiQuery<IStudioSettingsModel>({
    queryKey: [endpointsMetadata.studio.getStudioSettings, selectedStudioId],
    endpoint: `${endpointsMetadata.studio.getStudioSettings}`,
    enabled: selectedStudioId > 0,
    globalIsLoading: false,
  });

  const backToHome = () => {
    navigate(getDefaultRoutePath(currentUser));
  };

  useAddAdditionalLayoutButton({
    iconType: IconType.EditIcon,
    href: constructEditUserHref(Number(id) ?? 0),
    rawPath: routePaths.userEdit,
  });

  const generatePhoneNumberComponent = (): JSX.Element => {
    if (user?.phoneNumber) {
      return (
        <span className={styles.root_phoneNumber} data-onboarding="user-phone">
          <a href={`tel:${user?.phoneNumber}`}>{user?.phoneNumber}</a>
          <a
            href={`viber://contact?number=${encodeURIComponent(
              user?.phoneNumber,
            )}`}
          >
            <Icon type={IconType.Viber} width={30} height={30} />
          </a>
        </span>
      );
    }
    return <p>N/A</p>;
  };

  const generateEmailComponent = (): JSX.Element => {
    if (user?.email) {
      return <a href={`mailto:${user?.email}`}>{user?.email}</a>;
    }
    return <></>;
  };

  const { mutate: mutate } = useApiMutation();

  const onMarkAsUnreliable = () => {
    showCustomConfirmDialog(
      markAsUnreliableConfirmMessage,
      () =>
        mutate({
          endpoint: `${endpointsMetadata.user.toggleReliabilityStatus}?userId=${id}&isReliable=false`,
          globalIsLoading: true,
          method: "put",
          invalidateKeys: [[endpointsMetadata.user.getUser, id]],
        }),
      () => {},
      "warning",
      "Да",
      "Не",
    );
  };

  const onUnmarkAsUnreliable = () => {
    showCustomConfirmDialog(
      "Сигурни ли сте, че искате да маркирате потребителя като редовен?",
      () =>
        mutate({
          endpoint: `${endpointsMetadata.user.toggleReliabilityStatus}?userId=${id}&isReliable=true`,
          globalIsLoading: true,
          method: "put",
          invalidateKeys: [[endpointsMetadata.user.getUser, id]],
        }),
      () => {},
      "warning",
      "Да",
      "Не",
    );
  };

  // The rarer actions of the profile, behind the 3 dots at the end of the row.
  const moreActionsItems: IDropdownMenuItem[] = [
    ...(user
      ? [
          {
            label: "История",
            icon: IconType.ClockIcon,
            onClick: () => setShowHistoryPopup(true),
          },
        ]
      : []),
    ...(canRenewCard
      ? [
          {
            label: "Поднови карта",
            icon: IconType.CreditCard,
            onClick: () => setShowRenewCardPopup(true),
          },
        ]
      : []),
    // A deleted account can still be merged INTO another one, but not receive one.
    ...(user && userHasPermissionsToMergeUsers && !isStoreOnly && !user.isCancelled
      ? [
          {
            label: "Сливане с друг акаунт",
            icon: IconType.UsersIcon,
            className: styles.actionButtons_menuItem_usersIcon,
            onClick: () => setShowMergePopup(true),
          },
        ]
      : []),
    // The destructive one goes last.
    ...(userHasPermissionsToBulkDeleteUserEvents && !isStoreOnly
      ? [
          {
            label: "Перманентно изтриване на резервации",
            icon: IconType.DeleteIcon2,
            onClick: () => setShowBulkDeletePopup(true),
          },
        ]
      : []),
  ];

  const listActionButtons = (
    <div className={styles.actionButtons} data-onboarding="user-event-history">
      <span data-onboarding="user-notes">
        <NotesButton
          entityId={user?.id}
          label={"Бележки"}
          getEndpoint={endpointsMetadata.user.getNotes}
          createEndpoint={endpointsMetadata.user.addNote}
          updateEndpoint={endpointsMetadata.user.updateNote}
          deleteEndpoint={endpointsMetadata.user.deleteNote}
          noResultsLabel={
            "Към момента няма добавени бележки за този потребител."
          }
        />
      </span>
      <RenderWhen condition={!!userHasPermissionsToGeneratePasswordResetLink}>
        <span data-onboarding="user-generate-link">
          <IconButton
            iconType={IconType.Key}
            iconHeight={"25px"}
            iconWidth={"25px"}
            tooltip="Генериране и копиране на линк за възстановяване на парола (само мениджърите и собственика виждат тази опция)."
            onClick={() =>
              onTryGenerateAndCopyPasswordResetLink(Number(id ?? 0))
            }
          />
        </span>
      </RenderWhen>
      <span data-onboarding="user-unreliable">
        <RenderWhen condition={!user?.isUnreliable}>
          <IfTrue>
            <IconButton
              iconType={IconType.Cancel}
              iconHeight={"25px"}
              iconWidth={"25px"}
              tooltip="Потребителят в момента е редовен. Натиснете, за да го маркирате като НЕРЕДОВЕН."
              onClick={onMarkAsUnreliable}
            />
          </IfTrue>
          <IfFalse>
            <IconButton
              iconType={IconType.Valid}
              iconHeight={"25px"}
              iconWidth={"25px"}
              tooltip="Потребителят в момента е маркиран като НЕРЕДОВЕН. Натиснете, за да го отбележете като редовен."
              onClick={onUnmarkAsUnreliable}
            />
          </IfFalse>
        </RenderWhen>
      </span>
      {moreActionsItems.length > 0 && (
        <span
          className={styles.actionButtons_more}
          data-onboarding="user-more-actions"
        >
          <AppDropdownMenu
            items={moreActionsItems}
            triggerIcon={IconType.MoreOptions}
            triggerIconSize={"22px"}
            triggerIconColor={"#fff"}
            triggerClassName={styles.actionButtons_more_trigger}
          />
        </span>
      )}
    </div>
  );

  const onEventHistoryItemClick = (event: IEventHistoryItemViewModel) => {
    // Orders open in the cashdesk (where the full receipt lives); everything
    // else opens its planner event. `event.id` is the order id for order rows.
    const destination =
      event.kind === HistoryItemKind.Order
        ? constructCashdeskHref({ orderId: event.id })
        : constructPlannerEventHref(event.id);

    if (event.studioId !== selectedStudioId) {
      const targetStudio = userStudios.find((s) => s.id === event.studioId);
      // A global admin opening a real studio gets the access dialog instead (one question, logged).
      if (
        requiresStudioSwitchConfirmation(
          getCurrentUserRole(currentUser),
          targetStudio,
          selectedStudioId,
        )
      ) {
        requestStudioSwitch(event.studioId, () => navigate(destination));
        return;
      }
      if (targetStudio) {
        showCustomConfirmDialog(
          `Предстои смяна на обекта на "${targetStudio.name}". Искате ли да продължите?`,
          // Through the gate too, so a global admin leaving a real studio is logged here as well.
          () => requestStudioSwitch(event.studioId, () => navigate(destination)),
          () => {},
          "warning",
          "Да",
          "Не",
        );
        return;
      }
    }
    navigate(destination);
  };

  return {
    user,
    isLoading,
    isStoreOnly,
    showPlans,
    showBodyMetrics,
    showWorkouts,
    showGoals,
    showForms,
    tenantSettings,
    listActionButtons,
    showPlansPopup,
    showAdditionalDetailsPopup,
    showBulkDeletePopup,
    showDepositPopup,
    showFormsPopup,
    showHistoryPopup,
    showMergePopup,
    showRenewCardPopup,
    setShowPlansPopup,
    setShowAdditionalDetailsPopup,
    setShowBulkDeletePopup,
    setShowDepositPopup,
    setShowFormsPopup,
    setShowHistoryPopup,
    setShowMergePopup,
    setShowRenewCardPopup,
    depositBalanceText,
    depositBalanceTone,
    backToHome,
    generateEmailComponent,
    generatePhoneNumberComponent,
    navigate,
    onEventHistoryItemClick,
  };
};
